import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { X, Sparkles } from "lucide-react";
import TemplateDetailModal from "./TemplateDetailModal";
import AIRecommendationModal from "./AIRecommendationModal";
import ApproveHealthResultModal from "./ApproveHealthResultModal";
import {
  templateAPI,
  healthRecommendationAPI,
  healthRecordsAPI,
  referenceDataAPI,
} from "../services/api";
import { useAuth } from "../context/AuthContext";

const parseFlexibleDate = (value) => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(value);
  }

  const raw = String(value).trim();
  if (!raw) return null;

  const dateOnly = raw.split("T")[0].split(" ")[0];
  let parts = null;

  if (dateOnly.includes("-")) {
    parts = dateOnly.split("-");
  } else if (dateOnly.includes("/")) {
    parts = dateOnly.split("/");
  }

  if (parts?.length === 3) {
    let year;
    let month;
    let day;

    if (parts[0].length === 4) {
      year = Number.parseInt(parts[0], 10);
      month = Number.parseInt(parts[1], 10);
      day = Number.parseInt(parts[2], 10);
    } else {
      day = Number.parseInt(parts[0], 10);
      month = Number.parseInt(parts[1], 10);
      year = Number.parseInt(parts[2], 10);
    }

    if (year > 2400) {
      year -= 543;
    } else if (year < 100) {
      year += 2000;
    }

    const parsed = new Date(year, month - 1, day);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  }

  const fallback = new Date(raw);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
};

const calculateAgeAtDate = (birthday, referenceDate) => {
  const birthDate = parseFlexibleDate(birthday);
  const examDate = parseFlexibleDate(referenceDate);

  if (!birthDate || !examDate) {
    return null;
  }

  let age = examDate.getFullYear() - birthDate.getFullYear();
  const monthDiff = examDate.getMonth() - birthDate.getMonth();

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && examDate.getDate() < birthDate.getDate())
  ) {
    age -= 1;
  }

  if (age < 0 || age > 150) {
    return null;
  }

  return age;
};

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  opacity: ${(props) => (props.isOpen ? 1 : 0)};
  visibility: ${(props) => (props.isOpen ? "visible" : "hidden")};
  transition:
    opacity 0.3s ease,
    visibility 0.3s ease;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background-color: #ffffff;
  border-radius: 1rem;
  padding: 0;
  width: 95vw;
  max-width: 95vw;
  height: 90vh;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  transform: ${(props) => (props.isOpen ? "scale(1)" : "scale(0.95)")};
  transition: transform 0.3s ease;
  font-family: "Prompt", sans-serif;
`;

const ModalHeader = styled.div`
  padding: 2rem 2rem 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #ececec;
`;

const ModalTitle = styled.h2`
  color: #000000;
  font-size: 25px;
  font-weight: 400;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;

  svg {
    color: #cfd3d4;
    width: 14px;
    height: 14px;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
`;

const PatientInfoGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 2fr 3fr;
  gap: 1.5rem;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #ececec;
`;

const InfoColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const InfoItem = styled.div`
  font-size: 14px;
  color: #6f6f6f;
`;

const InfoLabel = styled.span`
  font-weight: 700;
`;

const InfoValue = styled.span`
  font-weight: 400;
`;

const HNValue = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: #6f6f6f;
`;

const ParameterColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ParameterTable = styled.div`
  display: grid;
  grid-template-columns: 150px 150px 120px 80px;
  gap: 0.5rem;
  margin-top: 1rem;
  justify-content: center;
`;

const CategoryHeader = styled.div`
  grid-column: 1 / -1;
  padding: 0.5rem 0 0.25rem 0;
  font-weight: 700;
  font-size: 11px;
  color: #4a4a4a;
  text-align: left;
  border-top: ${(props) => (props.isFirst ? "none" : "1px solid #e0e0e0")};
  margin-top: ${(props) => (props.isFirst ? "0" : "0.5rem")};
`;

const ParameterTableHeader = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: #6f6f6f;
  text-align: center;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #ececec;
`;

const ParameterTableCell = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: #6f6f6f;
  text-align: center;
  padding: 0.25rem 0;
`;

const ComparisonBadge = styled.div`
  font-size: 12px;
  font-weight: 500;
  text-align: center;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  display: inline-block;
  background-color: transparent;
  color: ${(props) => {
    if (props.status === "high") return "#DC2626";
    if (props.status === "low") return "#D97706";
    return "#059669";
  }};
`;

const ParameterHeader = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #6f6f6f;
  margin-bottom: 0.5rem;
`;

const ParameterItem = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: #6f6f6f;
`;

const TemplateTag = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: transparent;
  border: 1px solid #00b5b8;
  border-radius: 0px;
  padding: 4px 8px;
  margin: 2px;
  font-size: 14px;
  color: #00b5b8;
  cursor: pointer;
  position: relative;
  width: fit-content;

  &:hover {
    background-color: #00b5b8;
    color: white;
  }
`;

const RecommendationSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #ececec;
`;

const RecommendationColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const RecommendationTitle = styled.h3`
  font-size: 16px;
  font-weight: 400;
  color: #000000;
  margin: 0 0 1rem 0;
`;

const ImageSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const RecommendationImage = styled.div`
  width: 58px;
  height: 58px;
  background-color: #f3f4f6;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 12px;
`;

const EditImageButton = styled.button`
  background: none;
  border: 2px dashed #d1d5db;
  border-radius: 0.25rem;
  padding: 0.25rem 0.5rem;
  cursor: pointer;
  font-size: 12px;
  color: #6b7280;
  transition: all 0.2s;
  position: relative;

  &:hover {
    border-color: #00b5b8;
    color: #00b5b8;
  }
`;

const HiddenFileInput = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
`;

const RecommendationInput = styled.textarea`
  width: 270px;
  height: ${(props) => props.height || "200px"};
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  font-size: 14px;
  color: #111827;
  resize: none;
  font-family: "Prompt", sans-serif;

  &:focus {
    outline: none;
    border-color: #00b5b8;
  }
`;

const AIText = styled.div`
  font-size: 14px;
  color: #0f172a;
  margin-top: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    color: #06b6d4;
    width: 1rem;
    height: 1rem;
  }

  &:hover {
    color: #06b6d4;
  }
`;

const ModalFooter = styled.div`
  padding: 1.5rem;
  display: flex;
  justify-content: center;
  gap: 1rem;
`;

const CancelButton = styled.button`
  background-color: #f8f8f8;
  color: #6f6f6f;
  border: none;
  border-radius: 0.5rem;
  padding: 0.75rem 2rem;
  font-size: 14px;
  font-weight: 400;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: background-color 0.2s;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};

  &:hover {
    background-color: ${(props) => (props.disabled ? "#F8F8F8" : "#E5E7EB")};
  }
`;

const SubmitButton = styled.button`
  background-color: ${(props) => (props.disabled ? "#CFD3D4" : "#00B5B8")};
  color: #ffffff;
  border: none;
  border-radius: 0.5rem;
  padding: 0.75rem 2rem;
  font-size: 14px;
  font-weight: 400;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s;

  &:hover {
    background-color: ${(props) => (props.disabled ? "#CFD3D4" : "#0891B2")};
  }
`;

const HealthResultModal = ({ isOpen, onClose, selectedRecord, onApprove }) => {
  const { getUserFullName } = useAuth();
  const displayAge =
    calculateAgeAtDate(
      selectedRecord?.birthday ||
        selectedRecord?.birthdate ||
        selectedRecord?.birth_date,
      selectedRecord?.rawDate ||
        selectedRecord?.dateadd ||
        selectedRecord?.date,
    ) ?? selectedRecord?.age;
  const [recommendations, setRecommendations] = useState({
    doctor: "",
    food: "",
    exercise: "",
    sleep: "",
  });
  const [images, setImages] = useState({
    food: "food.jpg",
    exercise: "exercise.jpg",
    sleep: "sleep.jpg",
  });
  const [isTemplateDetailModalOpen, setIsTemplateDetailModalOpen] =
    useState(false);
  const [selectedTemplateForDetail, setSelectedTemplateForDetail] =
    useState(null);
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);
  const [isApproveConfirmModalOpen, setIsApproveConfirmModalOpen] =
    useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState("pending"); // เปลี่ยนเป็น state แทน
  const [dbRecordId, setDbRecordId] = useState(null); // เก็บ database record ID ที่แท้จริง
  const [masterData, setMasterData] = useState(null);

  // ฟังก์ชันเปรียบเทียบค่าปกติกับค่าที่ตรวจวัดได้
  const compareValues = (normalRange, resultValue, paramName = "") => {
    const normalizeComparisonParamName = (name) => {
      const aliases = {
        "Non-NPO blood glucose": "Non-NPO blood glucose",
        "Urine RBC": "RBC (Urine)",
        "Urine WBC": "WBC (Urine)",
        "Epithelial Cells": "Epithelial cell",
        "Atypical Lymphocyte": "Atypical Lymphocyte",
        Myeloblast: "Myeloblast",
        Promyelocyte: "Promyelocyte",
        Myelocyte: "Myelocyte",
        Metamyelocyte: "Metamyelocyte",
        Lymphoblast: "Lymphoblast",
        Spherocyte: "Spherocyte",
        "Cystine crystal": "Cystine crystal",
      };

      return aliases[name] || name;
    };

    const normalizedParamName = normalizeComparisonParamName(paramName);

    const parseComparableRange = (value) => {
      if (value === null || value === undefined) {
        return null;
      }

      const text = String(value).trim().replace(/,/g, "").replace(/\s+/g, "");
      const cleanedText = text.replace(
        /(cells\/hpf|cells\/lpf|\/hpf|\/lpf|cells\/ul|cells\/ฮผl|cells\/เธขเธ•l)$/i,
        "",
      );

      const rangeMatch = cleanedText.match(
        /^(-?\d+(?:\.\d+)?)-(-?\d+(?:\.\d+)?)/,
      );
      if (rangeMatch) {
        return {
          min: parseFloat(rangeMatch[1]),
          max: parseFloat(rangeMatch[2]),
          minInclusive: true,
          maxInclusive: true,
          raw: cleanedText,
        };
      }

      const lessThanMatch = cleanedText.match(/^<(-?\d+(?:\.\d+)?)/);
      if (lessThanMatch) {
        return {
          min: Number.NEGATIVE_INFINITY,
          max: parseFloat(lessThanMatch[1]),
          minInclusive: false,
          maxInclusive: false,
          raw: cleanedText,
        };
      }

      const greaterThanMatch = cleanedText.match(/^>(-?\d+(?:\.\d+)?)/);
      if (greaterThanMatch) {
        return {
          min: parseFloat(greaterThanMatch[1]),
          max: Number.POSITIVE_INFINITY,
          minInclusive: false,
          maxInclusive: false,
          raw: cleanedText,
        };
      }

      const singleValueMatch = cleanedText.match(/^(-?\d+(?:\.\d+)?)/);
      if (singleValueMatch) {
        const num = parseFloat(singleValueMatch[1]);
        return {
          min: num,
          max: num,
          minInclusive: true,
          maxInclusive: true,
          raw: cleanedText,
        };
      }

      return null;
    };

    const isRangeWithinRange = (resultRange, normalRange) => {
      // ผลตรวจแบบเปิดปลาย เช่น "<1.3" หรือ ">200" (พบบ่อยกับค่าบ่งชี้มะเร็งที่ต่ำ/สูงกว่าขีดจำกัดการตรวจวัด)
      // เช็คแบบ "ต้องอยู่ในช่วงทั้งหมด" ไม่ได้เพราะขอบเขตเป็นอนันต์ ให้เช็คว่าไม่มีทางซ้อนทับกันเลยแทน (ไม่ซ้อนทับ = ผิดปกติ)
      const resultIsOpenEnded =
        resultRange.min === Number.NEGATIVE_INFINITY ||
        resultRange.max === Number.POSITIVE_INFINITY;

      if (resultIsOpenEnded) {
        const noOverlap =
          resultRange.max < normalRange.min ||
          resultRange.min > normalRange.max;
        return !noOverlap;
      }

      const minWithin =
        resultRange.min > normalRange.min ||
        (resultRange.min === normalRange.min &&
          (normalRange.minInclusive || !resultRange.minInclusive));
      const maxWithin =
        resultRange.max < normalRange.max ||
        (resultRange.max === normalRange.max &&
          (normalRange.maxInclusive || !resultRange.maxInclusive));

      return minWithin && maxWithin;
    };
    if (
      !normalRange ||
      !resultValue ||
      normalRange === "xxx" ||
      resultValue === "xxx" ||
      normalRange === "-" ||
      resultValue === "-"
    ) {
      return { status: "normal", label: "-" };
    }

    if (normalizedParamName === "Stool Color") {
      return { status: "normal", label: "-" };
    }

    // ถ้า normalRange เป็น text (มีตัวอักษร แต่ไม่ใช่ format "M x-y, F x-y")
    const normalRangeText = String(normalRange).trim();
    const resultValueText = String(resultValue).trim();
    const normalRangeIsGenderFormat = /^[MF]\s/.test(normalRangeText);
    const isCountDropdownParam = [
      "RBC (Urine)",
      "WBC (Urine)",
      "Epithelial cell",
      "Red Blood Cells",
      "White Blood Cells",
      "WBC (Stool)",
      "RBC (Stool)",
    ].includes(normalizedParamName);

    if (isCountDropdownParam) {
      const normalizedNormal = parseComparableRange(normalRangeText);
      const normalizedResult = parseComparableRange(resultValueText);
      if (normalizedNormal && normalizedResult) {
        if (isRangeWithinRange(normalizedResult, normalizedNormal)) {
          return { status: "normal", label: "Normal" };
        }
        return { status: "high", label: "Abnormal" };
      }
    }

    if (/[a-zA-Z]/.test(normalRangeText) && !normalRangeIsGenderFormat) {
      const normalizedResult = resultValueText
        .toLowerCase()
        .replace(/\s+/g, "");
      const normalValues = normalRangeText
        .split(",")
        .map((v) => v.trim().toLowerCase().replace(/\s+/g, ""));
      if (normalValues.includes(normalizedResult)) {
        return { status: "normal", label: "Normal" };
      }
      return { status: "high", label: "High" };
    }

    const normalizedNormalRange = parseComparableRange(normalRangeText);
    const normalizedResultRange = parseComparableRange(resultValueText);

    // ถ้าทั้ง normalRange และ resultValue เป็นช่วงตัวเลข ต้องเทียบเป็นช่วงจริง
    // ไม่ใช่แค่เอาเลขตัวหน้าไป parseFloat เพราะจะทำให้ 2-3 ไปกลายเป็น 2
    if (normalizedNormalRange && normalizedResultRange) {
      if (isRangeWithinRange(normalizedResultRange, normalizedNormalRange)) {
        return { status: "normal", label: "Normal" };
      }

      return { status: "high", label: "Abnormal" };
    }

    const result = parseFloat(resultValue);
    if (isNaN(result)) {
      return { status: "normal", label: "-" };
    }

    if (normalRangeText.includes("-")) {
      const [min, max] = normalRangeText
        .split("-")
        .map((v) => parseFloat(v.trim()));
      if (result < min) {
        return { status: "low", label: "Low" };
      } else if (result > max) {
        return { status: "high", label: "High" };
      } else {
        return { status: "normal", label: "Normal" };
      }
    } else if (normalRangeText.startsWith("<")) {
      const max = parseFloat(normalRangeText.replace("<", "").trim());
      if (result >= max) {
        return { status: "high", label: "High" };
      } else {
        return { status: "normal", label: "Normal" };
      }
    } else if (normalRangeText.startsWith(">")) {
      const min = parseFloat(normalRangeText.replace(">", "").trim());
      if (result <= min) {
        return { status: "low", label: "Low" };
      } else {
        return { status: "normal", label: "Normal" };
      }
    }

    return { status: "normal", label: "Normal" };
  };

  // ฟังก์ชันแปลงชื่อพารามิเตอร์ให้ตรงกับ master data
  const normalizeParameterName = (paramName) => {
    const mapping = {
      // Blood Chemistry & Liver Function
      "SGOT (AST)": "SGOT (AST)",
      "SGPT (ALT)": "SGPT (ALT)",
      "ALP (Alkaline Phosphatase)": "ALP",
      ALP: "ALP",
      "Uric Acid": "Uric Acid",
      BUN: "BUN, Urea",
      GGT: "GGT",
      "Non-NPO blood glucose": "Non-NPO blood glucose",
      eAG: "eAG",
      Cholesterol: "คอเลสเตอรอลรวม",
      Triglyceride: "ไตรกลีเซอไรด์",
      CHO: "คอเลสเตอรอลรวม",
      TRI: "ไตรกลีเซอไรด์",
      FBS: "Fasting blood glucose",

      // CBC - Blood Cell Count
      Basophil: "Basophil",
      Platelet: "Platelets",
      "Platelets count": "Platelets count",
      Hypochromic: "Hypochromic",
      Microcyte: "Microcyte",
      Others: "Others",
      "Eosinophil %": "Eosinophil",
      Eosinophil: "Eosinophil",
      "Atypical Lymphocyte": "Atypical Lymphocyte",
      Myeloblast: "Myeloblast",
      Promyelocyte: "Promyelocyte",
      Myelocyte: "Myelocyte",
      Metamyelocyte: "Metamyelocyte",
      Lymphoblast: "Lymphoblast",
      Spherocyte: "Spherocyte",

      // Urine Tests
      "Urine Color": "Color",
      "Urine Appearance": "Urine Appearance",
      Appearance: "Urine Appearance",
      Clarity: "Clarity",
      "Urine pH": "pH",
      "Specific Gravity": "Specific Gravity",
      "Urine Protein": "Protein",
      Protein: "Protein",
      "Urine Glucose": "Sugar",
      Glucose: "Fasting blood glucose",
      "Glucose (FBS)": "Fasting blood glucose",
      "Urine Ketone": "Urine Ketone",
      Ketone: "Urine Ketone",
      "Urine Blood": "Blood",
      "Urine Leukocytes": "Leukocytes",
      "Urine Nitrite": "Nitrite",
      "Urine Bilirubin": "Bilirubin",
      Urobilinogen: "Urobilinogen",
      "Mucus thread": "Mucus thread",
      "Urine RBC": "RBC (Urine)",
      "Urine WBC": "WBC (Urine)",
      "Urine Bacteria": "Bacteria",
      Bacteria: "Bacteria",
      "Epithelial Cells": "Epithelial cell",
      Epithelial: "Epithelial cell",

      // Urine Crystals
      "Calcium Oxalate": [
        "Calcium oxalate crystal",
        "_hide_Calcium oxalate crystal",
      ],
      "Calcium oxalate": "Calcium oxalate crystal",
      "Uric Acid Crystal": "Uric acid crystal",
      "Uric acid": "Uric acid crystal",
      "Triple Phosphate": "Triple phosphate crystal",
      "Triple phosphate": "Triple phosphate crystal",
      "Calcium Carbonate crystal": "Calcium Carbonate crystal",
      "Calcium Carbonate": "Calcium Carbonate crystal",
      "Calcium carbonate": "Calcium Carbonate crystal",
      "Calcium Phosphate": "Calcium phosphate crystal",
      "Calcium phosphate": "Calcium phosphate crystal",
      Cystine: "Cystine crystal",
      "Cystine Crystal": "Cystine crystal",
      "Cystine crystal": "Cystine crystal",

      // Urine Casts
      "Hyaline Cast": "Hyaline cast",
      "Granular Cast": "Granular cast",
      "Cellular Cast": "Cellular cast",
      "Waxy Cast": "Waxy cast",
      "RBC cast": "RBC cast",

      // Stool Tests
      "Stool Color": "Color (Stool)",
      "Stool WBC": "WBC (Stool)",
      "Stool RBC": "RBC (Stool)",
      "Parasite, Ova": "Parasite",
      Parasite: "Parasite",
      Consistency: "Consistency",

      // Electrolytes
      Sodium: "Sodium",
      Potassium: "Potassium",
      Chloride: "Chloride",
      Bicarbonate: "Bicarbonate",
      "Total Calcium": "Total Calcium",
      Calcium: "Total Calcium",
      "Inorganic phosphate": "Inorganic phosphate",
      Magnesium: "Magnesium",

      // Tumor Markers
      AFP: "AFP",
      PSA: "PSA",
      CEA: "CEA",
      "CA19-9": "CA19-9",
      CA125: "CA125",

      // Hepatitis Virus
      HBsAb: "HBsAb",
      "Anti-HBs Concentration": "Anti-HBs Concentration",
      "HCV Ab": "HCV Ab",
      "anti-HCV": "HCV Ab",
      "HCV Ab COI": "COI",

      // Thyroid Hormones
      "Free T3": "Free T3",
      "Free T4": "Free T4",
      TSH: "TSH",

      // Other Tests
      Cholinesterase: "Cholinesterase",
      "Direct bilirubin": "Direct bilirubin",

      // Physical Examination
      BMI: "BMI",
      BPS: "BPS",
      BPD: "BPD",
      Pulse: "Pulse",
    };
    return mapping[paramName] || paramName;
  };

  // ฟังก์ชันจัดหมวดหมู่พารามิเตอร์ (ใช้หมวดหมู่จาก masterData/medium.json)
  const categorizeParameters = (parameters) => {
    if (!masterData || !parameters) {
      return { ทั้งหมด: parameters || [] };
    }

    const categorized = {};
    const usedParams = new Set();

    // ใช้หมวดหมู่จาก masterData (medium.json) โดยตรง
    for (const categoryName in masterData) {
      const categoryParams = masterData[categoryName];
      const paramNamesInCategory = Object.keys(categoryParams);

      // หา parameters ที่อยู่ในหมวดนี้
      categorized[categoryName] = parameters.filter((param) => {
        // ค้นหาแบบ case-insensitive
        const isMatch = paramNamesInCategory.some((masterParamName) => {
          // ลบ _hide_ prefix ออกก่อนเปรียบเทียบ
          const cleanMasterName = masterParamName.replace(/^_hide_/, "");

          // ใช้ normalizeParameterName เพื่อแปลงชื่อก่อนเปรียบเทียบ
          const normalizedParamName = normalizeParameterName(
            param.name,
          ).replace(/^_hide_/, "");
          const paramLower = normalizedParamName.toLowerCase();
          const masterLower = cleanMasterName.toLowerCase();
          const isMucusThread =
            paramLower.replace(/\s*\(urine\)$/, "").trim() === "mucus thread";

          // บังคับให้ Mucus thread อยู่ในหมวดผลการตรวจปัสสาวะเท่านั้น
          if (isMucusThread) {
            return categoryName === "ผลการตรวจปัสสาวะ";
          }

          // จับคู่แบบต่างๆ
          if (masterLower === paramLower) return true;

          // จับคู่แบบมี prefix "urine " หรือ suffix "(urine)"
          // ⚠️ ต้อง restrict ไว้เฉพาะหมวดผลการตรวจปัสสาวะเท่านั้น ไม่งั้นจะไปแมตช์ผิดหมวด
          // เช่น "RBC (Urine)" จะไปตรงกับ "RBC" ในหมวดความสมบูรณ์ของเลือดด้วย
          if (
            categoryName === "ผลการตรวจปัสสาวะ" &&
            paramLower.startsWith("urine ") &&
            paramLower.replace("urine ", "") === masterLower
          )
            return true;
          if (
            categoryName === "ผลการตรวจปัสสาวะ" &&
            paramLower.endsWith(" (urine)") &&
            paramLower.replace(" (urine)", "") === masterLower
          )
            return true;

          // จับคู่แบบมี suffix ในวงเล็บ เช่น "ALP (Alkaline Phosphatase)" -> "ALP"
          // แต่ไม่ใช้กับ qualifier ที่บ่งบอกประเภท เช่น "(Stool)", "(Urine)"
          const parenMatch = paramLower.match(/\(([^)]+)\)$/);
          const parenContent = parenMatch ? parenMatch[1].trim() : "";
          const typeQualifiers = ["stool", "urine", "serum", "blood"];
          const paramBase = paramLower.split(" (")[0].trim();
          if (
            paramBase === masterLower &&
            !typeQualifiers.includes(parenContent)
          )
            return true;

          // จับคู่แบบพิเศษ
          if (paramLower === "basophil" && masterLower === "basophile")
            return true;
          if (paramLower === "eosinophil %" && masterLower === "eosinophil")
            return true;
          if (
            paramLower === "epithelial cells" &&
            masterLower === "epithelial cell"
          )
            return true;
          if (paramLower === "epithelial" && masterLower === "epithelial cell")
            return true;

          return false;
        });

        if (isMatch) usedParams.add(param.name);
        return isMatch;
      });
    }

    // พารามิเตอร์ที่ไม่อยู่ในหมวดใดๆ ใส่ใน "อื่นๆ"
    const unusedParams = parameters.filter(
      (param) => !usedParams.has(param.name),
    );
    if (unusedParams.length > 0) {
      categorized["อื่นๆ"] = unusedParams;
    }

    // ลบหมวดที่ไม่มีพารามิเตอร์
    return Object.fromEntries(
      Object.entries(categorized).filter(([_, params]) => params.length > 0),
    );
  };

  // โหลด Master Data จาก API
  useEffect(() => {
    const loadMasterData = async () => {
      try {
        const result = await referenceDataAPI.getAllParameters();
        if (result.status === "success" && result.data) {
          setMasterData(result.data);
        }
      } catch (error) {
        console.error("Error loading master data:", error);
      }
    };

    loadMasterData();
  }, []);

  // Clear state เมื่อ selectedRecord เปลี่ยน (ก่อนโหลดข้อมูลใหม่)
  useEffect(() => {
    if (isOpen && selectedRecord?.id) {
      // Clear ข้อมูลเก่าเมื่อเปลี่ยนคน แต่ไม่ clear เมื่อเปิด modal ครั้งแรก
      setRecommendations({
        doctor: "",
        food: "",
        exercise: "",
        sleep: "",
      });
      setApprovalStatus("pending");
      setDbRecordId(null);
    }
  }, [selectedRecord?.id]); // trigger เฉพาะเมื่อ id เปลี่ยน

  // โหลด draft และ approval_status เมื่อเปิด modal
  useEffect(() => {
    if (isOpen && selectedRecord?.id) {
      // สร้าง/อัพเดท health record ในฐานข้อมูลก่อน (สำหรับ Option 2)
      // loadDraftAndStatus() จะโหลดข้อมูลที่บันทึกไว้มาแสดง
      createOrUpdateHealthRecord().then((recordId) => {
        if (recordId) {
          loadDraftAndStatus(recordId);
        }
      });
    } else if (!isOpen) {
      // Clear state เมื่อปิด modal เท่านั้น
      setRecommendations({
        doctor: "",
        food: "",
        exercise: "",
        sleep: "",
      });
      setApprovalStatus("pending");
      setDbRecordId(null);
    }
  }, [isOpen, selectedRecord?.id]);

  // ฟังก์ชันสร้าง/อัพเดท health record จากข้อมูล MFU API (Option 2)
  const createOrUpdateHealthRecord = async () => {
    try {
      const response = await healthRecordsAPI.createFromMFUData(selectedRecord);
      if (response.status === "success" && response.data?.record_id) {
        setDbRecordId(response.data.record_id);
        return response.data.record_id;
      }
    } catch (error) {
      console.error("Error creating health record:", error);
    }
    return null;
  };

  // ฟังก์ชันดึง draft และ approval_status ล่าสุดจาก DB
  const loadDraftAndStatus = async (recordId) => {
    try {
      const response = await healthRecommendationAPI.getDraft(recordId);
      if (response.status === "success" && response.data) {
        // อัปเดต recommendations
        setRecommendations({
          doctor: response.data.doctor_advice || "",
          food: response.data.nutrition_advice || "",
          exercise: response.data.exercise_advice || "",
          sleep: response.data.sleep_advice || "",
        });

        // อัปเดต approval_status จาก DB
        const latestStatus = response.data.approval_status || "pending";
        setApprovalStatus(latestStatus);
      } else {
        // ถ้าไม่มี draft ให้ clear recommendations และใช้สถานะ pending
        setRecommendations({
          doctor: "",
          food: "",
          exercise: "",
          sleep: "",
        });
        setApprovalStatus("pending");
      }
    } catch (error) {
      console.error("Error loading draft:", error);
      // ถ้า error ให้ clear recommendations และใช้สถานะ pending
      setRecommendations({
        doctor: "",
        food: "",
        exercise: "",
        sleep: "",
      });
      setApprovalStatus("pending");
    }
  };

  if (!selectedRecord) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose && onClose();
    }
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      // ต้องมี dbRecordId ก่อนถึงจะบันทึกได้ ถ้ายังไม่มีให้สร้างก่อน
      let recordIdToUse = dbRecordId;

      if (!recordIdToUse) {
        recordIdToUse = await createOrUpdateHealthRecord();
        if (!recordIdToUse) {
          alert("ไม่สามารถบันทึกได้ กรุณาลองใหม่อีกครั้ง");
          setIsSaving(false);
          return;
        }
      }

      const recommendationsData = {
        doctor_advice: recommendations.doctor,
        nutrition_advice: recommendations.food,
        exercise_advice: recommendations.exercise,
        sleep_advice: recommendations.sleep,
      };

      const response = await healthRecommendationAPI.saveDraft(
        recordIdToUse,
        recommendationsData,
        "admin",
      );

      if (response.status === "success") {
        // บันทึกสำเร็จ - ปิด modal อัตโนมัติ
        onClose && onClose();
      } else {
        alert("บันทึกไม่สำเร็จ: " + (response.message || "Unknown error"));
      }
    } catch (error) {
      console.error("Error saving recommendations:", error);
      alert("เกิดข้อผิดพลาดในการบันทึก");
    } finally {
      setIsSaving(false);
    }
  };

  const handleApproveClick = () => {
    console.log("🔥 handleApproveClick called - Opening confirmation modal");
    // เปิด confirmation modal
    setIsApproveConfirmModalOpen(true);
  };

  const handleApproveConfirm = async () => {
    console.log("🔥 handleApproveConfirm called - START");
    setIsSaving(true);
    try {
      // ต้องมี dbRecordId ก่อนถึงจะส่งได้ ถ้ายังไม่มีให้สร้างก่อน
      let recordIdToUse = dbRecordId;

      if (!recordIdToUse) {
        recordIdToUse = await createOrUpdateHealthRecord();
        if (!recordIdToUse) {
          alert("ไม่สามารถส่งผลสุขภาพได้ กรุณาลองใหม่อีกครั้ง");
          setIsSaving(false);
          return;
        }
      }

      const recommendationsData = {
        doctor_advice: recommendations.doctor,
        nutrition_advice: recommendations.food,
        exercise_advice: recommendations.exercise,
        sleep_advice: recommendations.sleep,
      };

      // แปลง template names เป็น template IDs
      const templateIds = selectedRecord?.matchedTemplates
        ? selectedRecord.matchedTemplates.map((mt) => mt.template_id)
        : [];

      // ดึง lineUserId จาก selectedRecord (ถ้ามี)
      const lineUserId =
        selectedRecord?.line_uid || selectedRecord?.lineUserId || null;

      // ✅ Snapshot ข้อมูลสุขภาพ ณ เวลาส่ง — ใช้แสดง public report โดยตรง ไม่ต้อง query MemberLabList ใหม่
      const healthSnapshot = selectedRecord
        ? {
            parameters:
              selectedRecord.sourceParameters ||
              selectedRecord.parameters ||
              [],
            sourceParameters:
              selectedRecord.sourceParameters ||
              selectedRecord.parameters ||
              [],
            labData: selectedRecord.labData || null,
            rawDate: selectedRecord.rawDate || null,
            patientName: selectedRecord.patientName || null,
            gender: selectedRecord.gender || null,
            age: displayAge || null,
            hn: selectedRecord.hn || null,
            nid: selectedRecord.nid || null,
            birthday: selectedRecord.birthday || null,
            weight: selectedRecord.weight || null,
            height: selectedRecord.height || null,
            bmi: selectedRecord.bmi || null,
            bps: selectedRecord.bps || null,
            bpd: selectedRecord.bpd || null,
            bloodPressure: selectedRecord.bloodPressure || null,
            pulse: selectedRecord.pulse || null,
            foodAllergies: selectedRecord.foodAllergies || null,
            diseases: selectedRecord.diseases || null,
            company: selectedRecord.company || null,
          }
        : null;

      // เรียก API พร้อมส่ง LINE Notification
      const response = await healthRecommendationAPI.approveAndSendWithLine(
        recordIdToUse,
        recommendationsData,
        getUserFullName(), // approved_by - ใช้ชื่อจริงของ user ที่ login
        templateIds, // ส่ง template IDs แทน template names
        lineUserId, // ส่ง lineUserId สำหรับแจ้งเตือนทาง LINE
        selectedRecord?.rawDate || null, // visit_dateadd: exact datetime from MemberLabList
        healthSnapshot, // ✅ snapshot ข้อมูลสุขภาพครบ
      );

      if (response.status === "success") {
        // ปิด modal ทันที แล้ว reload ข้อมูลใน background
        onClose && onClose();

        // reload ข้อมูล parent ใน background (ไม่ await)
        if (onApprove) {
          onApprove(selectedRecord?.id);
        }
      } else {
        alert("ส่งผลสุขภาพไม่สำเร็จ: " + (response.message || "Unknown error"));
      }
    } catch (error) {
      console.error("Error approving recommendations:", error);
      alert("เกิดข้อผิดพลาดในการส่งผลสุขภาพ");
    } finally {
      setIsSaving(false);
      setIsApproveConfirmModalOpen(false);
    }
  };

  const handleCloseApproveConfirmModal = () => {
    setIsApproveConfirmModalOpen(false);
  };

  const handleRecommendationChange = (field, value) => {
    setRecommendations((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleImageUpload = (field, event) => {
    const file = event.target.files[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setImages((prev) => ({
        ...prev,
        [field]: imageUrl,
      }));
    }
  };

  const handleTemplateClick = async (templateName) => {
    try {
      // ค้นหา template จาก API
      const response = await templateAPI.getAll();
      if (response.status === "success" && response.data) {
        const template = response.data.find(
          (t) => t.template_name === templateName,
        );

        if (template) {
          // แปลงข้อมูลให้ตรงกับรูปแบบที่ TemplateDetailModal ต้องการ
          const formattedTemplate = {
            id: template.id,
            templateName: template.template_name,
            createdBy: template.created_by,
            tags: template.tags || [],
            gender:
              template.gender === "male"
                ? "ชาย"
                : template.gender === "female"
                  ? "หญิง"
                  : "ทั้งสองเพศ",
            ageRange: `${template.age_min}-${template.age_max}`,
            parameters: template.parameters?.map((p) => p.parameter_name) || [],
            normalValues:
              template.parameters?.map((p) => p.normal_range || "-") || [],
            setValues:
              template.parameters?.map(
                (p) => `${p.custom_min}-${p.custom_max}`,
              ) || [],
          };

          setSelectedTemplateForDetail(formattedTemplate);
          setIsTemplateDetailModalOpen(true);
        }
      }
    } catch (error) {
      console.error("Error loading template:", error);
    }
  };

  const handleCloseTemplateDetailModal = () => {
    setIsTemplateDetailModalOpen(false);
    setSelectedTemplateForDetail(null);
  };

  const handleAIRecommendation = () => {
    setIsAIModalOpen(true);
  };

  const handleCloseAIModal = () => {
    setIsAIModalOpen(false);
  };

  const handleAIFinish = (aiData) => {
    // กรอกข้อมูล AI recommendations ลงในช่อง input ต่างๆ
    // Replace ทั้งหมดแทนการ merge เพื่อป้องกันข้อมูลเก่าติดค้าง
    setRecommendations({
      doctor: aiData.doctor_advice || aiData.doctorAdvice || "",
      food: aiData.nutrition_advice || aiData.dietAdvice || "",
      exercise: aiData.exercise_advice || aiData.exerciseAdvice || "",
      sleep: aiData.sleep_advice || aiData.sleepAdvice || "",
    });
  };

  return (
    <ModalOverlay isOpen={isOpen} onClick={handleOverlayClick}>
      <ModalContent isOpen={isOpen}>
        <ModalHeader>
          <div>
            <ModalTitle>ยืนยันการส่งผลสุขภาพ</ModalTitle>
            <div
              style={{
                fontSize: "14px",
                color: "#6F6F6F",
                marginTop: "0.5rem",
              }}
            >
              สถานะ:{" "}
              {approvalStatus === "approved"
                ? "อนุมัติและจัดส่งผลการตรวจสุขภาพแล้ว"
                : approvalStatus === "revised"
                  ? "มีการแก้ไขรอการอนุมัติอีกครั้ง"
                  : "รอการอนุมัติ"}
            </div>
          </div>
          <CloseButton onClick={onClose}>
            <X />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          {/* Patient Information - 4 Columns */}
          <PatientInfoGrid>
            <InfoColumn>
              <InfoItem>
                <InfoLabel>ชื่อ:</InfoLabel>{" "}
                <InfoValue>{selectedRecord?.patientName}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>เพศ:</InfoLabel>{" "}
                <InfoValue>{selectedRecord?.gender}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>อายุ:</InfoLabel> <InfoValue>{displayAge}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>โรคประจำตัว:</InfoLabel>{" "}
                <InfoValue>{selectedRecord?.diseases}</InfoValue>
              </InfoItem>
            </InfoColumn>

            <InfoColumn>
              <InfoItem style={{ fontWeight: "400" }}>HN</InfoItem>
              <HNValue>{selectedRecord?.hn}</HNValue>
            </InfoColumn>

            <ParameterColumn>
              <ParameterTable>
                <ParameterTableHeader>พารามิเตอร์</ParameterTableHeader>
                <ParameterTableHeader>ค่าปกติ</ParameterTableHeader>
                <ParameterTableHeader>ค่าที่ตรวจวัดได้</ParameterTableHeader>
                <ParameterTableHeader></ParameterTableHeader>

                {(() => {
                  const params = selectedRecord?.parameters || [];
                  const categorized = categorizeParameters(params);

                  return Object.entries(categorized).map(
                    ([category, categoryParams], catIndex) => (
                      <React.Fragment key={`cat-${catIndex}`}>
                        <CategoryHeader isFirst={catIndex === 0}>
                          {category}
                        </CategoryHeader>
                        {categoryParams.map((param, index) => {
                          const rawComparison = compareValues(
                            param.normal,
                            param.result,
                            param.name,
                          );
                          const isTextCategory =
                            category === "ผลการตรวจปัสสาวะ" ||
                            category === "ผลการตรวจอุจจาระ";
                          const comparison =
                            isTextCategory &&
                            rawComparison.label !== "-" &&
                            rawComparison.status !== "normal"
                              ? { status: "high", label: "Abnormal" }
                              : rawComparison;
                          return (
                            <React.Fragment key={`${catIndex}-${index}`}>
                              <ParameterTableCell>
                                {param.name}
                              </ParameterTableCell>
                              <ParameterTableCell>
                                {param.normal}
                              </ParameterTableCell>
                              <ParameterTableCell>
                                {param.result}
                              </ParameterTableCell>
                              <ParameterTableCell>
                                <ComparisonBadge status={comparison.status}>
                                  {comparison.label}
                                </ComparisonBadge>
                              </ParameterTableCell>
                            </React.Fragment>
                          );
                        })}
                      </React.Fragment>
                    ),
                  );
                })()}
              </ParameterTable>
            </ParameterColumn>

            <InfoColumn>
              <ParameterHeader>การแปลผลสุขภาพที่กำหนดไว้</ParameterHeader>
              <div>
                {selectedRecord?.templates &&
                selectedRecord.templates.length > 0 ? (
                  selectedRecord?.templates?.map((template, index) => (
                    <TemplateTag
                      key={index}
                      onClick={() => handleTemplateClick(template)}
                    >
                      {template}
                    </TemplateTag>
                  ))
                ) : (
                  <div
                    style={{
                      color: "#999",
                      fontSize: "12px",
                      marginTop: "8px",
                    }}
                  >
                    ไม่มี template ที่ match หรือยังไม่ได้เลือก
                  </div>
                )}
              </div>
            </InfoColumn>
          </PatientInfoGrid>

          {/* Recommendations - 4 Columns */}
          <RecommendationSection>
            <RecommendationColumn>
              <RecommendationTitle>คำแนะนำสุขภาพโดยแพทย์</RecommendationTitle>
              <RecommendationInput
                height="280px"
                value={recommendations.doctor}
                onChange={(e) =>
                  handleRecommendationChange("doctor", e.target.value)
                }
              />
            </RecommendationColumn>

            <RecommendationColumn>
              <RecommendationTitle>แนะนำการรับประทานอาหาร</RecommendationTitle>
              <ImageSection>
                <RecommendationImage
                  style={{
                    backgroundImage: images.food
                      ? `url(${images.food})`
                      : "none",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                >
                  {!images.food && "รูป"}
                </RecommendationImage>
                {/* <EditImageButton>
                  แก้ไขรูป
                  <HiddenFileInput
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload('food', e)}
                  />
                </EditImageButton> */}
              </ImageSection>
              <RecommendationInput
                value={recommendations.food}
                onChange={(e) =>
                  handleRecommendationChange("food", e.target.value)
                }
              />
            </RecommendationColumn>

            <RecommendationColumn>
              <RecommendationTitle>แนะนำการออกกำลังกาย</RecommendationTitle>
              <ImageSection>
                <RecommendationImage
                  style={{
                    backgroundImage: images.exercise
                      ? `url(${images.exercise})`
                      : "none",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                >
                  {!images.exercise && "รูป"}
                </RecommendationImage>
                {/* <EditImageButton>
                  แก้ไขรูป
                  <HiddenFileInput
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload("exercise", e)}
                  />
                </EditImageButton> */}
              </ImageSection>
              <RecommendationInput
                value={recommendations.exercise}
                onChange={(e) =>
                  handleRecommendationChange("exercise", e.target.value)
                }
              />
            </RecommendationColumn>

            <RecommendationColumn>
              <RecommendationTitle>แนะนำการนอน</RecommendationTitle>
              <ImageSection>
                <RecommendationImage
                  style={{
                    backgroundImage: images.sleep
                      ? `url(${images.sleep})`
                      : "none",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                >
                  {!images.sleep && "รูป"}
                </RecommendationImage>
                {/* <EditImageButton>
                  แก้ไขรูป
                  <HiddenFileInput
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload("sleep", e)}
                  />
                </EditImageButton> */}
              </ImageSection>
              <RecommendationInput
                value={recommendations.sleep}
                onChange={(e) =>
                  handleRecommendationChange("sleep", e.target.value)
                }
              />
              <AIText onClick={handleAIRecommendation}>
                รับคำแนะนำจากโปรแกรมอัตโนมัติ
                <Sparkles />
              </AIText>
            </RecommendationColumn>
          </RecommendationSection>
        </ModalBody>

        <ModalFooter>
          <CancelButton onClick={onClose} disabled={isSaving}>
            ยกเลิก
          </CancelButton>
          <SubmitButton onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? "กำลังบันทึก..." : "บันทึก"}
          </SubmitButton>
          <SubmitButton onClick={handleApproveClick} disabled={isSaving}>
            {isSaving ? "กำลังอนุมัติ..." : "อนุมัติและจัดส่งผลการตรวจสุขภาพ"}
          </SubmitButton>
        </ModalFooter>
      </ModalContent>

      <TemplateDetailModal
        isOpen={isTemplateDetailModalOpen}
        onClose={handleCloseTemplateDetailModal}
        templateData={selectedTemplateForDetail}
      />

      <AIRecommendationModal
        isOpen={isAIModalOpen}
        onClose={handleCloseAIModal}
        patientData={{ ...selectedRecord, dbRecordId: dbRecordId }}
        onFinish={handleAIFinish}
      />

      <ApproveHealthResultModal
        isOpen={isApproveConfirmModalOpen}
        onClose={handleCloseApproveConfirmModal}
        onConfirm={handleApproveConfirm}
      />
    </ModalOverlay>
  );
};

export default HealthResultModal;
